import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { motion } from 'framer-motion';
import './DashboardBultos.css';
import DashboardMenuHeader from '../components/TradeMenuHeader';
import { ChevronDown, Search, RefreshCw, Filter, FilterX, X, Tag, Users, Calendar, Download, Archive, SquarePen, CircleCheckBig, Images, X as XIcon } from 'lucide-react';
import { useQuery } from '../QueryContext';
import { useAuth } from '../AuthContext';
import { clearCache } from '../utils/dashboardCache';
import FullScreenLoader from './FullScreenLoader';
import GlobalSearch from '../components/GlobalSearch';

/* eslint-disable react-hooks/exhaustive-deps, no-unused-vars */


const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

const filterLabels = {
  T_SOLICITANTE: "Solicitante",
  T_ID_CLIENTE: "ID Cliente",
  T_CLI_PRIN: "Cliente Principal",
  T_CLI_NOMBRE: "Nombre Cliente",
  T_MARCA: "Marca",
  T_REGION: "Región A.",
  T_ACTIVIDAD: "Actividad",
  T_SUB_ACTIVIDAD: "Sub-Actividad",
  T_ASESOR_RELACIONADO: "Asesor Relacionado",
  T_ESTATUS: "Estatus",
  T_RANGO: "Horario / Rango",
  T_APERTURA: "Apertura",
  T_DIRECCION: "Dirección",
  T_MOTIVO: "Motivo",
  T_REQUERIMIENTO: "Requerimiento",
  ANIO: "Año",
  MES: "Mes",
  T_REG_SOL: "Reg. Solicitante"
};

// Opciones predefinidas movidas fuera para que sean accesibles globalmente en el archivo
const anioActual = new Date().getFullYear();

// Genera un array dinámico (ejemplo: los últimos 4 años contando el actual)
export const OPT_ANIO = Array.from({ length: 2 }, (_, index) => {
  const anio = anioActual - index;
  return { value: anio, label: String(anio) };
});

// El resultado hoy (2026) será: 2026, 2025, 2024, 2023

const OPT_MES = [
  { value: 1, label: "Enero" }, { value: 2, label: "Febrero" }, { value: 3, label: "Marzo" },
  { value: 4, label: "Abril" }, { value: 5, label: "Mayo" }, { value: 6, label: "Junio" },
  { value: 7, label: "Julio" }, { value: 8, label: "Agosto" }, { value: 9, label: "Septiembre" },
  { value: 10, label: "Octubre" }, { value: 11, label: "Noviembre" }, { value: 12, label: "Diciembre" }
];

const MultiSelectFilter = ({ label, name, icon: Icon, options = [], selectedValues = [], onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const totalOptions = options.length;
  const hasSelection = selectedValues?.length > 0;
  const allOptionsSelected = totalOptions > 0 && selectedValues.length === totalOptions;

  const displayValue = allOptionsSelected
    ? 'Todos'
    : hasSelection
      ? (selectedValues.length === 1
        ? options.find(opt => (opt.value || opt) === selectedValues[0])?.label || selectedValues[0]
        : `${selectedValues.length} Selec.`)
      : 'Todos';

  const showSearch = options.length > 12;
  const filteredOptions = options.filter(opt =>
    String(opt.label || opt).toLowerCase().includes(searchTerm.toLowerCase())
  ).slice(0, 100);

  const areAllSelected = filteredOptions.length > 0 && filteredOptions.every(opt =>
    selectedValues.includes(opt.value || opt)
  );

  const handleSelectAll = () => {
    if (areAllSelected) {
      const newSelected = selectedValues.filter(val =>
        !filteredOptions.some(opt => (opt.value || opt) === val)
      );
      onChange(name, newSelected);
    } else {
      const newValuesOnly = filteredOptions.map(opt => opt.value || opt);
      onChange(name, newValuesOnly);
    }
  };

  const handleSingleToggle = useCallback((optValue) => {
    const isPresent = selectedValues.some(v => String(v) === String(optValue));
    const newVals = isPresent
      ? selectedValues.filter(v => String(v) !== String(optValue))
      : [...selectedValues, optValue];
    onChange(name, newVals);
  }, [selectedValues, onChange, name]);

  return (
    <div className="filter-pill-container" ref={dropdownRef}>
      <button
        className={`filter-pill-button ${hasSelection ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <Icon size={12} className="pill-icon" />
        <span className="pill-label">{label}:</span>
        <span className="pill-value">{displayValue}</span>
        <ChevronDown size={11} className={`pill-arrow ${isOpen ? 'up' : ''}`} />
      </button>
      {isOpen && (
        <div className={`filter-dropdown-panel ${showSearch ? 'with-search' : ''}`}>
          {showSearch && (
            <div className="search-wrapper">
              <Search size={14} className="search-icon" />
              <input
                type="text"
                placeholder="Buscar..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
              />
            </div>
          )}
          {filteredOptions.length > 0 && (
            <label className="checkbox-row select-all-row">
              <input
                type="checkbox"
                checked={areAllSelected}
                onChange={handleSelectAll}
              />
              <span>Seleccionar Todos ({filteredOptions.length})</span>
            </label>
          )}
          <div className="options-list">
            {filteredOptions.map((opt, i) => {
              const optValue = opt.value ?? opt;
              const optLabel = opt.label ?? opt;
              const isChecked = selectedValues.some(v => String(v) === String(optValue));

              return (
                <label key={`${name}-${optValue}-${i}`} className="checkbox-row">
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => handleSingleToggle(optValue)}
                  />
                  <span>{optLabel}</span>
                </label>
              );
            })}
          </div>
          {hasSelection && (
            <button className="reset-pill-link" onClick={() => onChange(name, [])}>
              Limpiar ({selectedValues.length})
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const INITIAL_FILTERS = {
  T_SOLICITANTE: [], T_ID_CLIENTE: [], T_CLI_PRIN: [], T_CLI_NOMBRE: [],
  T_MARCA: [], T_REGION: [], T_ACTIVIDAD: [], T_SUB_ACTIVIDAD: [],
  T_ASESOR_RELACIONADO: [], T_ESTATUS: [], T_RANGO: [], T_APERTURA: [],
  ANIO: [], MES: [], T_REG_SOL: [],
};

const TradeFlow = () => {
  const { tradeflowQuery, getTradeFilters, updateTradeRegistro, loading: apiLoading } = useQuery();
  const { profile } = useAuth();

  const anioActual = new Date().getFullYear();
  const mesActual = new Date().getMonth() + 1;

  const [localLoading, setLocalLoading] = useState(true);
  const [querying, setQuerying] = useState(false);
  const [filters, setFilters] = useState({
    ...INITIAL_FILTERS,
    ANIO: [anioActual],
    MES: [mesActual]
  });
  const [availableOptions, setAvailableOptions] = useState({});
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [tradeConfig, setTradeConfig] = useState(null);
  const [registrosData, setRegistrosData] = useState([]);

  const [activeNavTab, setActiveNavTab] = useState('detalle');
  const [detailViewMode, setDetailViewMode] = useState('TOTAL');
  const [detailPage, setDetailPage] = useState(1);
  const [detailTotalPages, setDetailTotalPages] = useState(1);

  const [editingRowId, setEditingRowId] = useState(null);
  const [pendingEstatusById, setPendingEstatusById] = useState({});
  const [estatusDropdownOpenById, setEstatusDropdownOpenById] = useState({});

  const requestRef = useRef(0);
  const filtersInitialized = useRef(false);
  const debouncedFetch = useRef();

  // Modal imágenes evidencias (REVESTIMIENTOS)
  const [modalOpen, setModalOpen] = useState(false);
  const [modalImages, setModalImages] = useState([]);


  // --- CARGA INICIAL DESDE CONFIG DEL BACKEND ---
  useEffect(() => {
    const loadDashboardData = async () => {
      setLocalLoading(true);
      try {
        const config = await getTradeFilters();
        if (!config) throw new Error("Configuración de Trade Marketing no disponible.");

        setTradeConfig(config);
        setFilters({
          ...INITIAL_FILTERS,
          ANIO: [anioActual],
          MES: [mesActual]
        });

        // El merge ahora encuentra correctamente las constantes globales
        const mergedOpts = {
          ...(config.availableOptions || {}),
          ANIO: OPT_ANIO,
          MES: OPT_MES
        };
        setAvailableOptions(mergedOpts);
        filtersInitialized.current = true;
      } catch (err) {
        console.error('Error al cargar la configuración de filtros:', err);
      } finally {
        setLocalLoading(false);
      }
    };
    loadDashboardData();
  }, []);

  // Mantener MES/ANIO preseleccionados (mes y año actuales) tras la carga del backend.
  // Si el backend inicializa filters con vacíos, aquí lo reponemos.
  useEffect(() => {
    if (!filtersInitialized.current) return;
    setFilters(prev => {
      const anioActual = new Date().getFullYear();
      const mesActual = new Date().getMonth() + 1;
      return {
        ...prev,
        ANIO: (prev.ANIO && prev.ANIO.length > 0) ? prev.ANIO : [anioActual],
        MES: (prev.MES && prev.MES.length > 0) ? prev.MES : [mesActual]
      };
    });
  }, [filtersInitialized.current]);

  const handleRefresh = async () => {

    clearCache();
    await fetchData();
  };

  const handleResetFilters = useCallback(() => {
    setFilters({ ...INITIAL_FILTERS });
  }, []);

  const fetchData = useCallback(async (page = 1) => {
    if (!filtersInitialized.current) return;

    const requestId = ++requestRef.current;
    setQuerying(true);
    try {
      const payload = {
        filters: filters,
        page: page,
        user_info: profile ? { username: profile.username, cargo: profile.cargo } : null
      };

      const response = await tradeflowQuery(payload);
      if (requestId !== requestRef.current) return;

      if (response && response.success) {
        setRegistrosData(response.data || []);
        setDetailPage(page);
        setDetailTotalPages(response.totalPages || 1);
      }
    } catch (err) {
      if (requestId === requestRef.current) console.error("❌ Error en fetchData:", err);
    } finally {
      if (requestId === requestRef.current) setQuerying(false);
    }
  }, [filters, tradeflowQuery, profile]);

  useEffect(() => {
    if (!filtersInitialized.current) return;
    if (debouncedFetch.current) clearTimeout(debouncedFetch.current);
    debouncedFetch.current = setTimeout(() => { fetchData(1); }, 300);
    return () => clearTimeout(debouncedFetch.current);
  }, [filters, fetchData]);

  const fetchDetailData = (targetPage) => {
    fetchData(targetPage);
  };

  // --- FUNCIÓN ADAPTADA PARA GUARDAR EL NUEVO ESTATUS ---
  const handleUpdateField = async (rowId, fieldName, newValue) => {
    setRegistrosData(prevData =>
      prevData.map(row => row.ID === rowId ? { ...row, [fieldName]: newValue } : row)
    );

    try {
      const updatePayload = {
        ID: rowId,
        [fieldName]: newValue
      };
      const result = await updateTradeRegistro(updatePayload);
      if (result && result.success) {
        console.log(`✅ Actualización exitosa para ${fieldName} de ID ${rowId}`);
      } else {
        console.error(`❌ Fallo al actualizar ${fieldName} de ID ${rowId}:`, result?.detail || 'Error desconocido');
      }
    } catch (error) {
      console.error("Error al actualizar el registro en el servidor:", error);
    }
  };

  const downloadDetailExcel = (data) => {
    console.log("Descargando excel de Trade Flow...", data);
  };

  const dynamicFlowSteps = useMemo(() => {
    const uniqueStatuses = [...new Set(registrosData.map(item => String(item.T_ESTATUS || '').trim()).filter(Boolean))];

    if (uniqueStatuses.length === 0) return [];

    return uniqueStatuses.map((status, index) => {
      const total = uniqueStatuses.length;
      const ratio = total > 1 ? index / (total - 1) : 0;

      const r = Math.floor(0 + ratio * 150);
      const g = Math.floor(104 + ratio * 120);
      const b = Math.floor(179 - ratio * 140);
      const calculatedColor = `rgb(${r}, ${g}, ${b})`;

      const count = registrosData.filter(item => String(item.T_ESTATUS || '').trim() === status).length;

      return {
        key: status.toUpperCase(),
        label: status,
        color: calculatedColor,
        count: count
      };
    });
  }, [registrosData]);

  const pageVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setModalImages([]);
  };

  return (
    <motion.div
      className="dashboard-container"
      initial="initial" animate="animate" exit="exit" variants={pageVariants}
      transition={isMobile ? { duration: 0.3 } : { duration: 0.5, ease: "easeInOut" }}
    >
      <DashboardMenuHeader />

      {(apiLoading || localLoading) && !filtersInitialized.current ? (
        <FullScreenLoader message="Sincronizando flujos y líneas temporales de Trade..." />
      ) : null}

      {querying && <div className="querying-overlay active" />}

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <header className="header-flex">

          <div className="title-section">
            <h1 className="dashboard-title">Trade-Flow</h1>
            <div className="title-underline"></div>
          </div>
          <div className="date-selectors">
            <button className="refresh-button" onClick={handleRefresh} disabled={querying}>
              <RefreshCw size={16} className={querying ? "spin-anim" : ""} />
            </button>
            <button className="mobile-filter-btn" onClick={() => setShowMobileFilters(true)}>
              <Filter size={16} />
              <span>Filtros</span>
            </button>
            <div className="date-filters-group">
              <MultiSelectFilter
                label="Mes"
                name="MES"
                icon={Calendar}
                options={OPT_MES.map(m => ({ value: m.value, label: m.label }))}
                selectedValues={filters.MES || []}
                onChange={(name, vals) => setFilters(prev => ({ ...prev, MES: vals }))}
              />
              <MultiSelectFilter
                label="Año"
                name="ANIO"
                icon={Calendar}
                options={OPT_ANIO.map(y => ({ value: y.value, label: y.label }))}
                selectedValues={filters.ANIO || []}
                onChange={(name, vals) => setFilters(prev => ({ ...prev, ANIO: vals }))}
              />
            </div>
            <div style={isMobile ? { width: '100%', marginTop: '8px' } : {}}>
              <GlobalSearch
                availableOptions={availableOptions}
                filterLabels={filterLabels}
                filters={filters}
                setFilters={setFilters}
              />
            </div>
          </div>
        </header>

        {/* Panel lateral de filtros */}
        <div className={`filters-sidebar-container ${showMobileFilters ? 'open' : ''}`}>
          <div className="filters-sidebar-header">
            <h3>Filtros Disponibles</h3>
            <div className="filters-header-actions">
              <button className="reset-filters-btn" onClick={handleResetFilters}><FilterX size={16} /></button>
              <button className="close-filters-btn" onClick={() => setShowMobileFilters(false)}><X size={20} /></button>
            </div>
          </div>
          <div className="filters-grid" style={{ gap: '8px' }}>
            {Object.keys(filters)
              .filter((key) => key !== 'ANIO' && key !== 'MES')
              .map((key) => (
                <MultiSelectFilter
                  key={key}
                  label={filterLabels[key] || key}
                  name={key}
                  icon={key.includes('CLI') || key.includes('SOLICITANTE') ? Users : Tag}
                  options={availableOptions[key] || []}
                  selectedValues={filters[key] || []}
                  onChange={(name, vals) => setFilters(prev => ({ ...prev, [name]: vals }))}
                />
              ))}
          </div>
        </div>
        {showMobileFilters && <div className="filters-overlay" onClick={() => setShowMobileFilters(false)} />}

        {/* 📊 SECCIÓN DE PASOS DEL FLUJO */}
        {dynamicFlowSteps.length > 0 && (
          <div className="minimal-flow-canvas" style={{ margin: '40px 0', padding: '0 10px' }}>
            <div style={{ display: 'flex', width: '100%', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? '12px' : '0px', alignItems: 'stretch' }}>
              {dynamicFlowSteps.map((step, idx) => {
                const isSelected = filters.T_ESTATUS.includes(step.label);
                const isFirst = idx === 0;
                const isLast = idx === dynamicFlowSteps.length - 1;

                let clipPathStyle = 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)';
                if (!isMobile) {
                  if (isFirst) {
                    clipPathStyle = 'polygon(0% 0%, calc(100% - 24px) 0%, 100% 50%, calc(100% - 24px) 100%, 0% 100%)';
                  } else if (isLast) {
                    clipPathStyle = 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%, 24px 50%)';
                  } else {
                    clipPathStyle = 'polygon(0% 0%, calc(100% - 24px) 0%, 100% 50%, calc(100% - 24px) 100%, 0% 100%, 24px 50%)';
                  }
                }

                return (
                  <div
                    key={`flow-${step.label}`}
                    onClick={() => {
                      setFilters(prev => {
                        const active = prev.T_ESTATUS.includes(step.label);
                        return {
                          ...prev,
                          T_ESTATUS: active ? prev.T_ESTATUS.filter(s => s !== step.label) : [...prev.T_ESTATUS, step.label]
                        };
                      });
                    }}
                    style={{
                      width: '80%',
                      flex: 1,
                      position: 'relative',
                      background: step.color,
                      clipPath: clipPathStyle,
                      marginLeft: isMobile ? '0px' : (isFirst ? '0px' : '-18px'),
                      padding: isMobile ? '18px' : '15px 13px 15px 42px',
                      color: '#ffffff',
                      cursor: 'pointer',
                      display: 'flex',
                      borderRadius: isMobile ? '8px' : '10px',
                      flexDirection: 'column',
                      alignItems: isMobile ? 'flex-start' : 'center',
                      justifyContent: 'center',
                      opacity: filters.T_ESTATUS.length === 0 || isSelected ? 1 : 0.3,
                      transform: isSelected ? 'scale(1.02)' : 'scale(1)',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      zIndex: isSelected ? 30 : dynamicFlowSteps.length - idx
                    }}
                  >
                    <span style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '1.2px', textTransform: 'uppercase', opacity: 0.9 }}>
                      {step.label}
                    </span>
                    <span style={{ fontSize: '24px', fontWeight: '800', marginTop: '2px', fontFamily: 'sans-serif' }}>
                      {step.count}
                    </span>
                    {!isMobile && (
                      <div style={{
                        position: 'absolute', bottom: '0', left: '52%', width: '2px', height: '8px',
                        background: '#ffffff', opacity: isSelected ? 1 : 0.4
                      }} />
                    )}
                  </div>
                );
              })}
            </div>

            {!isMobile && (
              <div style={{ position: 'relative', width: '100%', height: '24px', display: 'flex', alignItems: 'center', marginTop: '4px' }}>
                <div style={{ position: 'absolute', left: '0', right: '0', height: '1px', borderTop: '2px dashed #334155', zIndex: 1 }} />
                <div style={{ display: 'flex', width: '100%', justifyContent: 'space-around', position: 'relative', zIndex: 2 }}>
                  {dynamicFlowSteps.map((step) => {
                    const isSelected = filters.T_ESTATUS.includes(step.label);
                    return (
                      <div
                        key={`node-${step.label}`}
                        style={{
                          width: '10px', height: '10px', borderRadius: '50%',
                          backgroundColor: step.color,
                          border: isSelected ? '2px solid #ffffff' : '2px solid #020617',
                          transform: isSelected ? 'scale(1.3)' : 'scale(1)',
                          boxShadow: isSelected ? `0 0 10px ${step.color}` : 'none',
                          transition: 'all 0.2s'
                        }}
                      />
                    );
                  })}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', width: '100%', height: '8px', borderRadius: '20px', overflow: 'hidden', marginTop: isMobile ? '12px' : '0px', background: '#1e293b' }}>
              {dynamicFlowSteps.map((step) => (
                <div
                  key={`track-${step.label}`}
                  style={{
                    flex: 1, background: step.color,
                    opacity: filters.T_ESTATUS.length === 0 || filters.T_ESTATUS.includes(step.label) ? 1 : 0.25,
                    transition: 'opacity 0.2s'
                  }}
                />
              ))}
            </div>
          </div>
        )}

        {/* 🎯 SECCIÓN DE SEGUIMIENTO DE REGISTROS TRADE */}
        {activeNavTab === 'detalle' && (
          <div className="detail-table-container">
            <div className="table-header">
              <h3>Registros del Flujo</h3>
              <div className="table-controls">
                <button
                  className="export-table-btn"
                  onClick={() => downloadDetailExcel(registrosData)}
                  disabled={querying || registrosData.length === 0}
                  title="Exportar a Excel"
                >
                  <Download size={18} />
                </button>

                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <button
                    className={`switch-toggle-btn ${detailViewMode === 'SIMPLIFICADO' ? 'active' : ''}`}
                    style={{ padding: '6px 10px' }}
                    onClick={() => setDetailViewMode('SIMPLIFICADO')}
                  >
                    Simplificado
                  </button>
                  <button
                    className={`switch-toggle-btn ${detailViewMode === 'TOTAL' ? 'active' : ''}`}
                    style={{ padding: '6px 10px' }}
                    onClick={() => setDetailViewMode('TOTAL')}
                  >
                    Completo
                  </button>
                </div>

                <span>Total: {registrosData.length}</span>

                <div className="pagination-controls">
                  <button
                    onClick={() => fetchDetailData(Math.max(1, detailPage - 1))}
                    disabled={detailPage === 1 || querying}
                  >
                    Anterior
                  </button>
                  <span>
                    Página {detailPage} de {detailTotalPages}
                  </span>
                  <button
                    onClick={() => fetchDetailData(page => page + 1)}
                    disabled={detailPage >= detailTotalPages || querying}
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            </div>

            {querying && registrosData.length === 0 ? (
              <div className="loading-table">
                <RefreshCw className="spin-anim" size={32} />
                <span>Cargando flujos...</span>
              </div>
            ) : registrosData.length === 0 ? (
              <div className="empty-state">
                <Archive size={48} />
                <h4>No hay registros de Trade Actividades</h4>
                <p>Ajuste los filtros para evaluar más consultas</p>
              </div>
            ) : (
              <div className="responsive-table-wrapper">
                <table className="detail-table2" style={{ fontSize: '0.6rem' }}>
                  <thead>
                    <tr>
                      {detailViewMode === 'SIMPLIFICADO' ? (
                        <>
                          <th>ID</th>
                          <th>Estatus</th>
                          <th>Fecha Registro</th>
                          <th>Solicitante</th>
                          <th>Región Solicitante</th>
                          <th>Nombre Cliente</th>
                          <th>Actividad</th>
                          <th>Acciones</th>
                        </>
                      ) : (
                        <>
                          <th>ID</th>
                          <th>Estatus</th>
                          <th>Fecha Registro</th>
                          <th>Solicitante</th>
                          <th>Reg. Solicitante</th>
                          <th>Nombre Cliente</th>
                          <th>Marca</th>
                          <th>Región A.</th>
                          <th>Asesor</th>
                          <th>Dirección</th>
                          <th>Actividad</th>
                          <th>Sub-Actividad</th>
                          <th>Horario/Rango</th>
                          <th>Apertura</th>
                          <th>Motivo</th>
                          <th>Requerimiento</th>
                          <th>Fecha Act.</th>
                          <th>Cant. Imp</th>
                          <th>Fotos Revest.</th>
                          <th>Acciones</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {registrosData.map((row, index) => {
                      const isSimplificado = detailViewMode === 'SIMPLIFICADO';
                      const isEditing = editingRowId === row.ID;

                      return (
                        <tr key={`trade-row-${row.ID || index}`}>
                          <td data-label="ID" className="font-medium">#{row.ID}</td>

                          <td data-label="Estatus">
                            {isEditing ? (
                              <div className="custom-dropdown-container" style={{ position: 'relative', width: '100%' }}>
                                <input
                                  type="text"
                                  readOnly
                                  value={pendingEstatusById[row.ID] ?? row.T_ESTATUS ?? 'REGISTRO'}
                                  onClick={() => {
                                    setEstatusDropdownOpenById(prev => ({
                                      ...prev,
                                      [row.ID]: !prev[row.ID]
                                    }));
                                  }}
                                  style={{
                                    width: '100%',
                                    textTransform: 'uppercase',
                                    cursor: 'pointer',
                                    border: '1px solid rgba(255,255,255,0.0)',
                                    borderRadius: '10px',
                                    background: 'transparent',
                                    boxShadow: '0 6px 16px rgba(0,0,0,0.55)',
                                    outline: 'none',
                                    padding: '5px',
                                    color: 'var(--text-third)',
                                    fontSize: '0.6rem'
                                  }}
                                />
                                {estatusDropdownOpenById[row.ID] && (
                                  <ul className="dropdown-menu-list2">
                                    {['REGISTRO', 'EN PROCESO', 'APROBADO', 'COMPLETADO', 'RECHAZADO'].map((opt, i) => (
                                      <li
                                        key={`estatus-${row.ID}-${opt}-${i}`}
                                        className="dropdown-item2"
                                        onClick={() => {
                                          setPendingEstatusById(prev => ({ ...prev, [row.ID]: opt }));
                                        }}
                                      >
                                        {opt}
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            ) : (
                              <span className={`status-pill ${String(row.T_ESTATUS || 'REGISTRO').toLowerCase().replace(/\s+/g, '-')}`}>
                                {row.T_ESTATUS || 'REGISTRO'}
                              </span>
                            )}

                          </td>

                          <td data-label="Fecha Registro">{row.FECHA_REGISTRO || '-'}</td>
                          <td data-label="Solicitante">{row.T_SOLICITANTE || '-'}</td>
                          <td data-label="Reg. Solicitante">{row.T_REG_SOL || '-'}</td>

                          {isSimplificado ? (
                            <>
                              <td data-label="Nombre Cliente">{row.T_CLI_NOMBRE || '-'}</td>
                              <td data-label="Actividad">{row.T_ACTIVIDAD || '-'}</td>
                            </>
                          ) : (
                            <>
                              <td data-label="Nombre Cliente">{row.T_CLI_NOMBRE || '-'}</td>
                              <td data-label="Marca">{row.T_MARCA || '-'}</td>
                              <td data-label="Región A.">{row.T_REGION || '-'}</td>
                              <td data-label="Asesor Relacionado">{row.T_ASESOR_RELACIONADO || '-'}</td>
                              <td data-label="Dirección" className="truncate-cell" title={row.T_DIRECCION}>{row.T_DIRECCION || '-'}</td>
                              <td data-label="Actividad">{row.T_ACTIVIDAD || '-'}</td>
                              <td data-label="Sub-Actividad">{row.T_SUB_ACTIVIDAD || '-'}</td>
                              <td data-label="Horario/Rango">{row.T_RANGO || '-'}</td>
                              <td data-label="Apertura">{row.T_APERTURA || '-'}</td>
                              <td data-label="Motivo" className="truncate-cell" title={row.T_MOTIVO}>{row.T_MOTIVO || '-'}</td>
                              <td data-label="Requerimiento" className="truncate-cell" title={row.T_REQUERIMIENTO}>{row.T_REQUERIMIENTO || '-'}</td>
                              <td data-label="Fecha Actividad" className="truncate-cell" title={row.T_FECHA_ACT}>{row.T_FECHA_ACT || '-'}</td>
                              <td data-label="Cant. Imp." className="truncate-cell" title={row.T_CANT_IMP}>{row.T_CANT_IMP || '-'}</td>
                              <td data-label="Fotos Revest." style={{ textAlign: 'center' }}>
                                {(row.T_ACTIVIDAD || '').toUpperCase() === 'REVESTIMIENTOS' && row.T_REVE_EVIDENCIAS && row.T_REVE_EVIDENCIAS.length > 0 ? (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      // abrir modal usando state local
                                      setModalImages(row.T_REVE_EVIDENCIAS);
                                      setModalOpen(true);
                                    }}
                                    style={{
                                      background: 'transparent',
                                      border: 'none',
                                      padding: 0,
                                      cursor: 'pointer',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                    title="Ver imágenes"
                                  >
                                    <Images size={18} color="#ffffff" />
                                  </button>
                                ) : (
                                  '-'
                                )}
                              </td>
                            </>
                          )}

                          <td data-label="Acciones">
                            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', alignItems: 'center' }}>
                              {isEditing ? (
                                <>
                                  <button
                                    className="update-row-btn"
                                    title="Guardar cambios"
                                    onClick={() => {
                                      const nextValue =
                                        pendingEstatusById[row.ID] ?? row.T_ESTATUS ?? 'REGISTRO';
                                      handleUpdateField(row.ID, 'T_ESTATUS', nextValue);
                                      setEditingRowId(null);
                                      setPendingEstatusById(prev => {
                                        const copy = { ...prev };
                                        delete copy[row.ID];
                                        return copy;
                                      });
                                      setEstatusDropdownOpenById(prev => {
                                        const copy = { ...prev };
                                        delete copy[row.ID];
                                        return copy;
                                      });
                                    }}
                                  >
                                    <CircleCheckBig size={27} />
                                  </button>
                                  <button
                                    className="cancel-row-btn"
                                    title="Cancelar"
                                    onClick={() => {
                                      setEditingRowId(null);
                                      setPendingEstatusById(prev => {
                                        const copy = { ...prev };
                                        delete copy[row.ID];
                                        return copy;
                                      });
                                      setEstatusDropdownOpenById(prev => {
                                        const copy = { ...prev };
                                        delete copy[row.ID];
                                        return copy;
                                      });
                                    }}
                                  >
                                    <X size={27} />
                                  </button>
                                </>
                              ) : (
                                <button
                                  className="edit-row-btn"
                                  title="Editar estatus"
                                  onClick={() => {
                                    setEditingRowId(row.ID);
                                    setPendingEstatusById(prev => ({
                                      ...prev,
                                      [row.ID]: row.T_ESTATUS ?? 'REGISTRO'
                                    }));
                                    setEstatusDropdownOpenById(prev => {
                                      const copy = { ...prev };
                                      copy[row.ID] = false; // aseguramos que NO esté desplegado al entrar
                                      return copy;
                                    });
                                  }}
                                >
                                  <SquarePen size={28} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </motion.div>

      {modalOpen && (
        <div
          onClick={handleCloseModal}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0,0,0,0.75)',
            zIndex: 99999,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '90%',
              maxWidth: 900,
              background: '#111827',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: 12,
              padding: 16,
              boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 12
              }}
            >
              <div style={{ color: '#fff', fontWeight: '800' }}>Fotos Revestimiento</div>
              <button
                type="button"
                onClick={handleCloseModal}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#fff'
                }}
                title="Cerrar"
              >
                <XIcon size={22} />
              </button>
            </div>

            <div style={{ maxHeight: '70vh', overflow: 'auto' }}>
              {modalImages?.length > 0 ? (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                    gap: 12
                  }}
                >
                  {modalImages.map((url, idx) => (
                    <div
                      key={idx}
                      style={{ borderRadius: 10, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.12)' }}
                    >
                      <img
                        src={url}
                        alt={`evidencia-${idx}`}
                        style={{ width: '100%', height: 240, objectFit: 'cover', display: 'block' }}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ color: '#fff', opacity: 0.8 }}>No hay imágenes.</div>
              )}
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};
export default TradeFlow;
